/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useEffect, useRef } from 'react';
import { NOTIFICATION_TYPES } from '@vbee-holding/aivoice-shared-lib';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { StyledDrawer } from '@src/containers/MobileTTS/NewVoiceDrawer/index.styled';
import NotificationHeader from '@src/components/NotificationPanel/NotificationHeader';
import NotificationTabs from '@src/components/NotificationPanel/NotificationTabs';
import NotificationList from '@src/components/NotificationPanel/NotificationList';
import NotificationDetail from '@src/components/NotificationPanel/NotificationDetail';
import {
  fetchNotifications,
  fetchGroupsUnreadStatus,
  setActiveGroup,
  markRead,
  markAllRead,
} from '@src/redux/notification/actions';

const DrawNotification = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const [selectedNotification, setSelectedNotification] = React.useState(null);
  const isInitialized = useRef(false);

  const { activeGroup, unreadStatus, groups } = useSelector(
    (state) => state.notification,
  );
  const currentGroup = groups[activeGroup] || {
    list: [],
    cursor: '',
    hasMore: true,
    loading: false,
  };
  const { list: notifications, cursor, hasMore, loading } = currentGroup;

  useEffect(() => {
    dispatch(fetchGroupsUnreadStatus());
  }, []);

  useEffect(() => {
    if (!open) {
      isInitialized.current = false;
      return;
    }

    if (isInitialized.current) return;

    let targetGroup = activeGroup;

    let maxSendTime = 0;
    let groupWithMaxSendTime = NOTIFICATION_TYPES.PROMOTION;

    Object.entries(groups).forEach(([groupCode, group]) => {
      if (group.list && group.list.length > 0) {
        group.list.forEach((notification) => {
          const sendTime = new Date(notification.sentAt).getTime();
          if (sendTime > maxSendTime) {
            maxSendTime = sendTime;
            groupWithMaxSendTime = groupCode;
          }
        });
      }
    });

    targetGroup = groupWithMaxSendTime;
    dispatch(setActiveGroup(targetGroup));

    dispatch(fetchNotifications({ groupCode: targetGroup }));
    dispatch(markAllRead(targetGroup));

    isInitialized.current = true;
  }, [open, unreadStatus, dispatch, groups, activeGroup]);

  const handleTabChange = useCallback(
    (group) => {
      dispatch(setActiveGroup(group));
      dispatch(markAllRead(group));
      dispatch(fetchNotifications({ groupCode: group }));
    },
    [dispatch],
  );

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      dispatch(
        fetchNotifications({
          groupCode: activeGroup,
          cursor,
          isLoadMore: true,
        }),
      );
    }
  }, [dispatch, loading, hasMore, cursor, activeGroup]);

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllRead());
  }, [dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchNotifications({ groupCode: activeGroup }));
  }, [dispatch, activeGroup]);

  const handleItemClick = useCallback(
    (notification) => {
      if (!notification.readAt) {
        dispatch(markRead(notification.id));
      }

      const linkRedirectUrl = notification?.rawContent?.[language]?.redirectUrl;
      if (linkRedirectUrl) {
        window.open(linkRedirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        setSelectedNotification(notification);
      }
    },
    [dispatch],
  );

  const handleDetailClose = useCallback(() => {
    setSelectedNotification(null);
  }, []);

  // ================== Render ==================
  return (
    <>
      <StyledDrawer
        title={t('chooseVoice')}
        anchor="bottom"
        open={open}
        onClose={onClose}
        style={{
          padding: '16px',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <NotificationHeader
            onClose={onClose}
            onMarkAllRead={handleMarkAllRead}
          />
          <NotificationTabs
            activeGroup={activeGroup}
            unreadStatus={unreadStatus}
            onTabChange={handleTabChange}
          />
          <NotificationList
            notifications={notifications}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onItemClick={handleItemClick}
            onRefresh={handleRefresh}
          />
        </div>
      </StyledDrawer>

      {selectedNotification && (
        <NotificationDetail
          notification={selectedNotification}
          onClose={handleDetailClose}
        />
      )}
    </>
  );
};

export default DrawNotification;
